import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { TalentUserAvatar } from '../../../../../../common/ui/talent-user-avatar';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import { useLogException } from '../../../../../../controllers/error-boundary/utils';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchTalentPosition_AtlassianSearchPage$key } from './__generated__/searchTalentPosition_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { PositionFocusArea } from './position-focus-area';
import type { positionFocusArea_AtlassianSearchPage$key } from './position-focus-area/__generated__/positionFocusArea_AtlassianSearchPage.graphql';

export const SearchTalentPosition = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	searchQuery,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchTalentPosition_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const logException = useLogException();
	const data = useFragment(
		graphql`
			fragment searchTalentPosition_AtlassianSearchPage on SearchResult {
				__typename

				id
				type
				url
				... on SearchResultTalentPosition {
					__typename
					position {
						__typename
						worker {
							preferredName
							user {
								picture
							}
						}
						fieldValues(
							fieldIdIsIn: [
								"positionId"
								"positionStatus"
								"positionJobTitle"
								"positionPositionTitle"
								"positionKey"
								"positionExternalPositionKey"
								"positionWorker"
								"focusArea"
							]
						) {
							fieldId
							fieldValue {
								__typename
								... on RadarStringFieldValue {
									__typename
									stringValue: value
								}
								... on RadarAriFieldValue {
									__typename
									value {
										__typename
										... on MercuryFocusArea {
											...positionFocusArea_AtlassianSearchPage @dangerously_unaliased_fixme
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const preferredName: string | undefined = data.position?.worker?.preferredName || undefined;

	const [jobTitle, positionExternalKey, focusArea, positionKey] = useMemo(() => {
		let jobTitle = '';
		let positionExternalKey = '';
		let focusArea: positionFocusArea_AtlassianSearchPage$key | null = null;
		let positionKey = '';

		data.position?.fieldValues?.forEach((fieldValue) => {
			if (
				fieldValue.fieldId === 'positionKey' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				positionKey = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'positionPositionTitle' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				jobTitle = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'positionExternalPositionKey' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				positionExternalKey = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'focusArea' &&
				fieldValue.fieldValue?.__typename === 'RadarAriFieldValue' &&
				fieldValue.fieldValue?.value?.__typename === 'MercuryFocusArea'
			) {
				focusArea = fieldValue.fieldValue?.value;
			}
		});

		return [jobTitle, positionExternalKey, focusArea, positionKey];
	}, [data.position?.fieldValues]);

	const title = useMemo(() => {
		const title = formatMessage(messages.positionTitle, {
			positionKey: positionKey,
			preferredName: preferredName,
		});
		if (!searchQuery) {
			return enrichExcerpt(title) || title;
		}

		const { highlightedTitle } = highlightMatchedText(title, searchQuery.split(' '));
		return !highlightedTitle ? enrichExcerpt(title) : enrichExcerpt(highlightedTitle);
	}, [positionKey, preferredName, formatMessage, searchQuery]);

	const headerMetadataParts = useMemo(
		() => [
			['type', <SearchResultType type={data.type as SearchClientResultType} />],
			jobTitle ? ['positionTitle', jobTitle] : null,
			positionExternalKey
				? [
						'positionExternalKey',
						formatMessage(messages.positionExternalKey, { positionExternalKey }),
					]
				: null,
		],
		[data.type, jobTitle, positionExternalKey, formatMessage],
	);

	if (!data.position) {
		logException(`SearchResultRenderer: Invalid Talent Position - ${data.id}`);
		return null;
	}

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					hideSummarizeInChat={true}
					product={ProductKeys.Talent}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={data.url}
					title={title}
					icon={<TalentUserAvatar src={data.position?.worker?.user?.picture} />}
					headerMetadataParts={headerMetadataParts}
					description={<PositionFocusArea node={focusArea} />}
					cloudId={cloudId}
					position={position}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchTalentPosition = (
	node?: any,
): node is searchTalentPosition_AtlassianSearchPage$key => {
	return node?.id?.includes('radar') && node.__typename === 'SearchResultTalentPosition';
};
